import { useCallback, useState } from 'react';

export type CopyState = 'idle' | 'copying' | 'copied' | 'error';

/** 写入剪贴板；不支持 Clipboard API 时回退到隐藏 textarea + execCommand。 */
export async function copyText(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand('copy');
  textarea.remove();
  if (!copied) throw new Error('copy failed');
}

/**
 * 复制动作的状态机。`resolve` 可以是同步文本，也可以是按需拉全文的异步函数——
 * 批次生成结果只带摘要，需要点击时才请求版本正文。
 */
export function useCopyAction(): {
  state: CopyState;
  copy: (resolve: () => string | Promise<string>) => Promise<boolean>;
  reset: () => void;
} {
  const [state, setState] = useState<CopyState>('idle');

  const copy = useCallback(async (resolve: () => string | Promise<string>) => {
    setState('copying');
    try {
      await copyText(await resolve());
      setState('copied');
      return true;
    } catch {
      setState('error');
      return false;
    }
  }, []);

  const reset = useCallback(() => setState('idle'), []);

  return { state, copy, reset };
}

/** 标题与正文拼成可直接粘贴到平台编辑器的文本。 */
export function composeDraftClipboardText(
  title: string | null | undefined,
  body: string | null | undefined,
): string {
  return [title ?? '', body ?? ''].join('\n\n');
}
